import React, { useState, useEffect, useCallback, useRef } from 'react';
import { smsAPI, conversationsAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  MessageSquare, Send, Loader2, ArrowLeft, Phone,
  ChevronRight, Clock, CheckCircle2, AlertCircle, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { formatTimestampCompact, getTenantTimezone, parseUTC } from '../lib/timezone';

function formatTime(dateStr) {
  return formatTimestampCompact(dateStr);
}

function StatusIcon({ status }) {
  if (!status) return null;
  const s = status.toLowerCase();
  if (['delivered', 'read'].includes(s)) return <CheckCircle2 className="w-3 h-3 text-emerald-500" />;
  if (['sent', 'queued'].includes(s)) return <Clock className="w-3 h-3 text-slate-400" />;
  if (['failed', 'undelivered'].includes(s)) return <AlertCircle className="w-3 h-3 text-red-500" />;
  return <Clock className="w-3 h-3 text-slate-400" />;
}

// ---- Conversation List ----
function ConversationList({ conversations, loading, selectedId, onSelect }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!conversations.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
        <MessageSquare className="w-8 h-8" />
        <p className="text-sm">No conversations yet</p>
        <p className="text-xs">Send an SMS to start a conversation</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800">
      {conversations.map(conv => (
        <button
          key={conv.id}
          data-testid={`conversation-item-${conv.id}`}
          onClick={() => onSelect(conv)}
          className={cn(
            "w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors",
            selectedId === conv.id && "bg-blue-50 dark:bg-blue-950/30 border-l-2 border-blue-500"
          )}
        >
          <div className="flex items-center justify-between mb-0.5">
            <span className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
              {conv.contact_name || conv.contact_phone || 'Unknown'}
            </span>
            <span className="text-xs text-slate-400 ml-2 whitespace-nowrap">
              {formatTime(conv.last_activity_at)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {conv.channel === 'voice' ? (
              <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
            ) : (
              <MessageSquare className="w-3 h-3 text-slate-400 flex-shrink-0" />
            )}
            <span className="text-xs text-slate-500 truncate">
              {conv.last_message_body || (conv.channel === 'voice' ? 'Voice call' : 'No messages')}
            </span>
            <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
              {conv.message_count}
            </Badge>
          </div>
        </button>
      ))}
    </div>
  );
}

// ---- Message Thread ----
function MessageThread({ messages, loading }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
        <MessageSquare className="w-8 h-8" />
        <p className="text-sm">No messages in this conversation</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3 overflow-y-auto" style={{ maxHeight: '420px' }}>
      {messages.map(msg => {
        const isOutbound = msg.direction === 'outbound';
        const isVoice = msg.channel === 'voice';

        return (
          <div
            key={msg.id}
            data-testid={`message-${msg.id}`}
            className={cn("flex", isOutbound ? "justify-end" : "justify-start")}
          >
            <div className={cn(
              "max-w-[75%] rounded-2xl px-3.5 py-2 text-sm",
              isOutbound
                ? "bg-blue-600 text-white rounded-br-md"
                : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-md"
            )}>
              {isVoice ? (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" />
                  <span>{msg.duration ? `Call (${Math.floor(msg.duration / 60)}m ${msg.duration % 60}s)` : 'Voice call'}</span>
                </div>
              ) : (
                <p className="whitespace-pre-wrap break-words">{msg.body}</p>
              )}
              <div className={cn(
                "flex items-center gap-1 mt-1",
                isOutbound ? "justify-end" : "justify-start"
              )}>
                <span className={cn(
                  "text-[10px]",
                  isOutbound ? "text-blue-200" : "text-slate-400"
                )}>
                  {msg.created_at ? new Intl.DateTimeFormat('en-US', { timeZone: getTenantTimezone(), hour: 'numeric', minute: '2-digit' }).format(parseUTC(msg.created_at)) : ''}
                </span>
                {isOutbound && <StatusIcon status={msg.status} />}
                {msg.user_name && (
                  <span className={cn(
                    "text-[10px]",
                    isOutbound ? "text-blue-200" : "text-slate-400"
                  )}>
                    {msg.user_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

// ---- Compose Bar ----
function ComposeBar({ customerId, jobId, customerPhones, activeConvPhone, onSent }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Destination phone for the current send. The backend resolves this
  // server-side from `contact_id`, but we display the most likely target
  // here so the user can see *who* they're texting before pressing send.
  //
  // Mismatch detection: if the currently-open conversation's contact_phone
  // (server-derived) doesn't match any of the customer's stored phones,
  // we surface an amber hint so the user knows the conversation is on a
  // stale phone — usually because the customer changed numbers, or the
  // record was attached to an orphan thread without updating
  // `primary_phone`. This is the friction point that caused replies to
  // land in "Unmatched" instead of threading correctly.
  const storedPhones = React.useMemo(
    () => [customerPhones?.primary, customerPhones?.secondary, customerPhones?.billing]
      .filter(Boolean)
      .map(p => (p || '').replace(/\D/g, '').replace(/^1?/, '1')),
    [customerPhones]
  );
  const destinationPhone = activeConvPhone || customerPhones?.primary || '';
  const destinationDigits = (destinationPhone || '').replace(/\D/g, '').replace(/^1?/, '1');
  const mismatch = Boolean(
    destinationDigits && storedPhones.length > 0 && !storedPhones.includes(destinationDigits)
  );

  useEffect(() => {
    smsAPI.templates.list().then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId || templateId === 'custom') {
      setShowPreview(false);
      setPreviewText('');
      return;
    }
    try {
      const res = await smsAPI.preview({
        contact_id: customerId,
        job_id: jobId || undefined,
        template_id: templateId,
        message_body: '',
      });
      setPreviewText(res.data.preview_text);
      setText(res.data.preview_text);
      setShowPreview(true);
    } catch {
      toast.error('Failed to preview template');
    }
  };

  const handleSend = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await smsAPI.send({
        contact_id: customerId,
        message_body: body,
        job_id: jobId || undefined,
      });
      setText('');
      setSelectedTemplateId('');
      setShowPreview(false);
      setPreviewText('');
      onSent();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send SMS';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
      {templates.length > 0 && (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400" />
          <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
            <SelectTrigger className="h-8 text-xs w-52" data-testid="template-selector">
              <SelectValue placeholder="Use template..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">Custom message</SelectItem>
              {templates.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Destination phone display. Shown so the user has visual proof
          of who they're texting before sending — closes the silent
          mismatch class of bug where outbound goes to a phone that
          isn't on the customer's record. */}
      {destinationPhone && (
        <div
          className={cn(
            'text-[11px] flex items-center gap-1.5 px-2',
            mismatch ? 'text-amber-700 dark:text-amber-300' : 'text-slate-500 dark:text-slate-400'
          )}
          data-testid="sms-destination-phone"
        >
          <Phone className="w-3 h-3" />
          <span>
            Sending to <span className="font-medium tabular-nums">{destinationPhone}</span>
          </span>
          {mismatch && (
            <span
              className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 text-[10px] font-medium"
              data-testid="sms-destination-mismatch"
              title={`This number is not on the customer's record. Stored: ${(customerPhones?.primary || '—')} / ${(customerPhones?.secondary || '—')} / ${(customerPhones?.billing || '—')}. Replies will land in this thread, but updating the customer's primary phone is recommended.`}
            >
              not on customer record
            </span>
          )}
        </div>
      )}
      {showPreview && previewText && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2 text-xs text-amber-800 dark:text-amber-200">
          <span className="font-medium">Preview: </span>{previewText}
        </div>
      )}
      <div className="flex items-end gap-2">
        <Textarea
          data-testid="sms-compose-input"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Type a message..."
          className="resize-none text-sm min-h-[40px] max-h-[120px]"
          rows={1}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          data-testid="sms-send-button"
          size="icon"
          onClick={handleSend}
          disabled={!text.trim() || sending}
          className="h-10 w-10 shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// ---- Main Component ----
export default function CustomerMessagesTab({
  customerId, customerName, jobId, customerPhones,
  // URL-driven selection (parent owns the URL state; we just sync with it
  // so a hard reload deep-links straight back into the same thread).
  selectedConversationId = null,
  onSelectConversation = null,
}) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Live-refresh cadences. SMS is human-scale, so a 5s message poll and
  // a 10s thread-list poll keep latency snappy without thrashing the API.
  // Polling is paused when the tab/window is hidden — saves API calls
  // for an operator who left the page open in a background tab.
  const MESSAGES_POLL_MS = 5000;
  const CONVERSATIONS_POLL_MS = 10000;

  const fetchConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await conversationsAPI.list({ contact_id: customerId, channel: 'sms' });
      setConversations(res.data || []);
      return res.data || [];
    } catch {
      if (!silent) toast.error('Failed to load conversations');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMessages = useCallback(async (convId, { silent = false } = {}) => {
    if (!silent) setMessagesLoading(true);
    try {
      const res = await conversationsAPI.messages(convId);
      setMessages(res.data || []);
    } catch {
      if (!silent) toast.error('Failed to load messages');
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedConv) fetchMessages(selectedConv.id);
  }, [selectedConv, fetchMessages]);

  // URL ↔ state sync — when `selectedConversationId` (driven by ?conv=)
  // changes, restore that conversation. Runs on mount (so deep links
  // work) and on browser back/forward.
  useEffect(() => {
    if (!selectedConversationId) {
      if (selectedConv) {
        setSelectedConv(null);
        setMessages([]);
      }
      return;
    }
    if (selectedConv?.id === selectedConversationId) return;
    // Look up the conversation in our already-loaded list. If we haven't
    // loaded the list yet, the next `fetchConversations` resolve will
    // re-run this effect and pick it up.
    const found = conversations.find(c => c.id === selectedConversationId);
    if (found) setSelectedConv(found);
  }, [selectedConversationId, conversations, selectedConv]);

  // Live refresh — message poll for the open thread.
  useEffect(() => {
    if (!selectedConv?.id) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      fetchMessages(selectedConv.id, { silent: true });
    };
    const handle = setInterval(tick, MESSAGES_POLL_MS);
    // Also refresh immediately when the tab becomes visible again
    const onVis = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        fetchMessages(selectedConv.id, { silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(handle);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [selectedConv?.id, fetchMessages]);

  // Live refresh — conversation list poll, so a brand-new inbound from
  // a customer who didn't have an SMS thread yet appears without
  // forcing a manual reload.
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      fetchConversations({ silent: true });
    };
    const handle = setInterval(tick, CONVERSATIONS_POLL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, [fetchConversations]);

  const handleSent = () => {
    fetchConversations({ silent: true });
    if (selectedConv) fetchMessages(selectedConv.id, { silent: true });
  };

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    if (onSelectConversation) onSelectConversation(conv?.id || null);
  };

  const handleBack = () => {
    setSelectedConv(null);
    setMessages([]);
    if (onSelectConversation) onSelectConversation(null);
  };

  // If no conversations and not loading, show the compose-first view
  const showComposeFirst = !loading && conversations.length === 0;

  return (
    <Card data-testid="customer-messages-tab" className="overflow-hidden">
      <div className="flex h-[560px]">
        {/* Left panel — conversation list */}
        <div className={cn(
          "w-72 border-r border-slate-200 dark:border-slate-700 flex flex-col shrink-0",
          selectedConv && "hidden md:flex"
        )}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">Messages</h3>
            <p className="text-xs text-slate-500">SMS conversations</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ConversationList
              conversations={conversations}
              loading={loading}
              selectedId={selectedConv?.id}
              onSelect={selectConversation}
            />
          </div>
        </div>

        {/* Right panel — thread + compose */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedConv ? (
            <>
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-8 w-8"
                  onClick={handleBack}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100">
                    {selectedConv.contact_name || selectedConv.contact_phone || 'Unknown'}
                  </p>
                  <p className="text-xs text-slate-500">{selectedConv.contact_phone}</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <MessageThread messages={messages} loading={messagesLoading} />
              </div>
              <ComposeBar
                customerId={customerId}
                jobId={jobId}
                customerPhones={customerPhones}
                activeConvPhone={selectedConv.contact_phone}
                onSent={handleSent}
              />
            </>
          ) : showComposeFirst ? (
            <div className="flex-1 flex flex-col">
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3 p-6">
                <MessageSquare className="w-10 h-10" />
                <p className="text-sm font-medium">Start a conversation</p>
                <p className="text-xs text-center">
                  Send an SMS to {customerName || 'this customer'} to begin
                </p>
              </div>
              <ComposeBar
                customerId={customerId}
                jobId={jobId}
                customerPhones={customerPhones}
                activeConvPhone={null}
                onSent={handleSent}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Select a conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
